import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material'
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material'
import { billingApi, userService, institutionService } from '../../services/api'
import { BillingEntry, UserWithProfile, Institution } from '../../types/api'
import { useAuth } from '../../contexts/AuthContext'
import TenantBillingApprovalsTab from './TenantBillingApprovalsTab'
import toast from 'react-hot-toast'

const MediciApprovalsTab: React.FC = () => {
  const { user } = useAuth()
  const [billingEntries, setBillingEntries] = useState<BillingEntry[]>([])
  const [totalEntries, setTotalEntries] = useState(0)
  const [, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  
  // Filters
  const [medicFilter, setMedicFilter] = useState('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState<'segment' | 'hour' | ''>('')
  const [statusFilter, setStatusFilter] = useState<'draft' | 'approved' | 'rejected' | 'paid' | ''>('draft')
  
  // Date filters
  const [monthFilter, setMonthFilter] = useState<number>(new Date().getMonth() + 1)
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear())
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false)
  
  // Dialog state
  const [approvalDialog, setApprovalDialog] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<BillingEntry | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [processing, setProcessing] = useState(false)
  
  // Dropdown data
  const [medics, setMedics] = useState<UserWithProfile[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])

  // Load billing entries
  const loadBillingEntries = async () => {
    try {
      setLoading(true)
      
      // Calculate from_date and to_date when filter is enabled
      let fromDate: string | undefined = undefined
      let toDate: string | undefined = undefined
      
      if (dateFilterEnabled) {
        // First day of month
        fromDate = `${yearFilter}-${monthFilter.toString().padStart(2, '0')}-01`
        
        // Last day of month
        const lastDay = new Date(yearFilter, monthFilter, 0).getDate()
        toDate = `${yearFilter}-${monthFilter.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`
      }
      
      const response = await billingApi.getBillingEntries({
        doctor_id: medicFilter || undefined,
        institution_id: institutionFilter || undefined,
        billing_type: typeFilter || undefined,
        status: statusFilter || undefined,
        from_date: fromDate,
        to_date: toDate,
        page: page + 1,
        size: rowsPerPage
      })
      
      setBillingEntries(response.entries)
      setTotalEntries(response.total)
    } catch (error) {
      console.error('Error loading billing entries:', error)
      toast.error('Eroare la încărcarea plăților')
    } finally {
      setLoading(false)
    }
  }

  // Load dropdown data
  const loadDropdownData = async () => {
    try {
      const [medicsData, institutionsData] = await Promise.all([
        userService.getUsers({ role: 'medic', limit: 200 }),
        institutionService.getInstitutions({ limit: 200 })
      ])
      
      setMedics(medicsData.users)
      setInstitutions(institutionsData.institutions)
    } catch (error) {
      console.error('Error loading dropdown data:', error)
      toast.error('Eroare la încărcarea datelor')
    }
  }

  useEffect(() => {
    loadBillingEntries()
  }, [page, rowsPerPage, medicFilter, institutionFilter, typeFilter, statusFilter, monthFilter, yearFilter, dateFilterEnabled])

  useEffect(() => {
    loadDropdownData()
  }, [])

  const handleApprove = async (entry: BillingEntry) => {
    try {
      setProcessing(true)
      await billingApi.approveBillingEntry(entry.id, { approved: true })
      toast.success('Plată aprobată cu succes')
      loadBillingEntries()
    } catch (error: any) {
      console.error('Error approving entry:', error)
      toast.error(error.response?.data?.detail || 'Eroare la aprobarea plății')
    } finally {
      setProcessing(false)
    }
  }

  const handleReject = (entry: BillingEntry) => {
    setSelectedEntry(entry)
    setRejectionReason('')
    setApprovalDialog(true)
  }

  const handleRejectConfirm = async () => {
    if (!selectedEntry || !rejectionReason.trim()) {
      toast.error('Motivul respingerii este obligatoriu')
      return
    }
    
    try {
      setProcessing(true)
      await billingApi.approveBillingEntry(selectedEntry.id, {
        approved: false,
        rejection_reason: rejectionReason
      })
      toast.success('Plată respinsă')
      setApprovalDialog(false)
      loadBillingEntries()
    } catch (error: any) {
      console.error('Error rejecting entry:', error)
      toast.error(error.response?.data?.detail || 'Eroare la respingerea plății')
    } finally {
      setProcessing(false)
    }
  }

  const getBillingTypeLabel = (type: string) => {
    switch (type) {
      case 'segment': return 'Segmente'
      case 'hour': return 'Ore'
      default: return type
    }
  }

  const getBillingTypeColor = (type: string) => {
    switch (type) {
      case 'segment': return 'secondary'
      case 'hour': return 'primary'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'În așteptare'
      case 'approved': return 'Aprobat'
      case 'rejected': return 'Respins'
      case 'paid': return 'Plătit'
      default: return status
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'warning'
      case 'approved': return 'success'
      case 'rejected': return 'error'
      case 'paid': return 'info'
      default: return 'default'
    }
  }

  if (!user || !['admin', 'operator'].includes(user.role)) {
    return (
      <Alert severity="error">
        Nu aveți permisiuni pentru a accesa această pagină.
      </Alert>
    )
  }

  // Count pending approvals
  const pendingCount = billingEntries.filter(entry => entry.status === 'draft').length

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        {pendingCount > 0 && (
          <Chip
            label={`${pendingCount} în așteptare`}
            color="warning"
            variant="filled"
          />
        )}
      </Box>

      {/* Stats Cards - compact */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
            <CardContent>
              <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                În așteptare
              </Typography>
              <Typography variant="h5" color="warning.main" sx={{ fontSize: '1.5rem' }}>
                {billingEntries.filter(e => e.status === 'draft').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
            <CardContent>
              <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Aprobate
              </Typography>
              <Typography variant="h5" color="success.main" sx={{ fontSize: '1.5rem' }}>
                {billingEntries.filter(e => e.status === 'approved').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
            <CardContent>
              <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Respinse
              </Typography>
              <Typography variant="h5" color="error.main" sx={{ fontSize: '1.5rem' }}>
                {billingEntries.filter(e => e.status === 'rejected').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
            <CardContent>
              <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Total Sumă
              </Typography>
              <Typography variant="h5" color="primary.main" sx={{ fontSize: '1.5rem' }}>
                {billingEntries.reduce((sum, e) => sum + (typeof e.total_amount === 'number' ? e.total_amount : parseFloat(e.total_amount) || 0), 0).toFixed(2)} RON
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters - compact */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                label="Status"
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
                <MenuItem value="draft" sx={{ fontSize: '0.8rem' }}>În așteptare</MenuItem>
                <MenuItem value="approved" sx={{ fontSize: '0.8rem' }}>Aprobate</MenuItem>
                <MenuItem value="rejected" sx={{ fontSize: '0.8rem' }}>Respinse</MenuItem>
                <MenuItem value="paid" sx={{ fontSize: '0.8rem' }}>Plătite</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Tip</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                label="Tip"
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
                <MenuItem value="segment" sx={{ fontSize: '0.8rem' }}>Segmente</MenuItem>
                <MenuItem value="hour" sx={{ fontSize: '0.8rem' }}>Ore</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Medic</InputLabel>
              <Select
                value={medicFilter}
                onChange={(e) => setMedicFilter(e.target.value)}
                label="Medic"
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toți medicii</MenuItem>
                {medics.map((medic) => (
                  <MenuItem key={medic.id} value={medic.id} sx={{ fontSize: '0.8rem' }}>
                    {medic.doctor_profile?.first_name} {medic.doctor_profile?.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}>
              <InputLabel>Instituție</InputLabel>
              <Select
                value={institutionFilter}
                onChange={(e) => setInstitutionFilter(e.target.value)}
                label="Instituție"
              >
                <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate instituțiile</MenuItem>
                {institutions.map((institution) => (
                  <MenuItem key={institution.id} value={institution.id} sx={{ fontSize: '0.8rem' }}>
                    {institution.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
              onClick={loadBillingEntries}
              fullWidth
              sx={{ fontSize: '0.78rem', py: 0.75 }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Date Filter - compact inline */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 0.5 }}>📅 Perioadă</Typography>
          
          <IconButton 
            size="small"
            sx={{ p: 0.25 }}
            onClick={() => {
              if (monthFilter === 1) {
                setMonthFilter(12)
                setYearFilter(yearFilter - 1)
              } else {
                setMonthFilter(monthFilter - 1)
              }
              setDateFilterEnabled(true)
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
          
          <FormControl size="small" sx={{ minWidth: 100, '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 } }}>
            <Select
              value={monthFilter}
              onChange={(e) => {
                setMonthFilter(Number(e.target.value))
                setDateFilterEnabled(true)
              }}
            >
              {[
                'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
                'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
              ].map((month, index) => (
                <MenuItem key={index} value={index + 1} sx={{ fontSize: '0.75rem' }}>{month}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 70, '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 } }}>
            <Select
              value={yearFilter}
              onChange={(e) => {
                setYearFilter(Number(e.target.value))
                setDateFilterEnabled(true)
              }}
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                <MenuItem key={year} value={year} sx={{ fontSize: '0.75rem' }}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <IconButton 
            size="small"
            sx={{ p: 0.25 }}
            onClick={() => {
              if (monthFilter === 12) {
                setMonthFilter(1)
                setYearFilter(yearFilter + 1)
              } else {
                setMonthFilter(monthFilter + 1)
              }
              setDateFilterEnabled(true)
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
          
          <Button 
            size="small" 
            variant="outlined"
            onClick={() => {
              setMonthFilter(new Date().getMonth() + 1)
              setYearFilter(new Date().getFullYear())
              setDateFilterEnabled(true)
            }}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1 }}
          >
            Luna Curentă
          </Button>
          
          <Button 
            size="small" 
            variant={dateFilterEnabled ? 'contained' : 'outlined'}
            color={dateFilterEnabled ? 'error' : 'primary'}
            onClick={() => setDateFilterEnabled(false)}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1 }}
          >
            {dateFilterEnabled ? 'Șterge' : 'Toate Perioadele'}
          </Button>
        </Box>
      </Paper>

      {/* Billing entries table - compact with dark header */}
      <Paper sx={{
        '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 },
        '& .MuiChip-root': { height: 20, fontSize: '0.75rem' },
        '& .MuiTableCell-root': { py: 0.5, px: 1 },
        '& .MuiTableBody-root .MuiIconButton-root': { p: '3px' },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.900',
                '& th': {
                  color: 'common.white',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  py: 1,
                  px: 1.5,
                  whiteSpace: 'nowrap'
                }
              }}>
                <TableCell>Data</TableCell>
                <TableCell>Medic</TableCell>
                <TableCell>Instituție</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Descriere</TableCell>
                <TableCell align="right">Sumă</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billingEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.8rem' }}>
                      {new Date(entry.billing_date).toLocaleDateString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem' }} color="text.secondary">
                      {new Date(entry.created_at).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {entry.doctor_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.institution_name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getBillingTypeLabel(entry.billing_type)}
                      color={getBillingTypeColor(entry.billing_type) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.quantity} × {entry.unit_price} {entry.currency}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {typeof entry.total_amount === 'number' ? entry.total_amount.toFixed(2) : 
                       (isNaN(parseFloat(entry.total_amount)) ? '0.00' : parseFloat(entry.total_amount).toFixed(2))} {entry.currency}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(entry.status)}
                      color={getStatusColor(entry.status) as any}
                      size="small"
                    />
                    {entry.status === 'rejected' && entry.rejection_reason && (
                      <Typography variant="caption" color="error" display="block">
                        {entry.rejection_reason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {entry.status === 'draft' && (
                      <>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleApprove(entry)}
                          disabled={processing}
                        >
                          <ApproveIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleReject(entry)}
                          disabled={processing}
                        >
                          <RejectIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalEntries}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          labelRowsPerPage="Plăți per pagină"
        />
      </Paper>

      {/* Rejection Dialog */}
      <Dialog
        open={approvalDialog}
        onClose={() => setApprovalDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Respinge Plată
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Medic:</strong> {selectedEntry.doctor_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Sumă:</strong> {selectedEntry.total_amount} {selectedEntry.currency}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Descriere:</strong> {selectedEntry.description}
              </Typography>
            </Box>
          )}
          
          <TextField
            fullWidth
            label="Motivul respingerii *"
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Introduceți motivul pentru respingerea acestei plăți..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>
            Anulează
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={processing || !rejectionReason.trim()}
          >
            Respinge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Wrapper component with tabs - compact styling matching BillingReportsPage
const ApprovalsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'medici' | 'institutii'>('medici')

  return (
    <Box>
      <Tabs 
        value={activeTab} 
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ 
          minHeight: 28,
          borderBottom: 1, 
          borderColor: 'divider', 
          mb: 1.5,
          '& .MuiTab-root': {
            minHeight: 28,
            py: 0.25,
            px: 1,
            fontSize: '0.75rem',
            textTransform: 'none',
            '& .MuiSvgIcon-root': {
              fontSize: '0.85rem',
              mr: 0.5
            }
          }
        }}
      >
        <Tab 
          icon={<PeopleIcon />} 
          iconPosition="start" 
          label="Plăți Medici" 
          value="medici" 
        />
        <Tab 
          icon={<BusinessIcon />} 
          iconPosition="start" 
          label="Plăți Instituții" 
          value="institutii" 
        />
      </Tabs>

      {activeTab === 'medici' && <MediciApprovalsTab />}
      {activeTab === 'institutii' && <TenantBillingApprovalsTab />}
    </Box>
  )
}

export default ApprovalsPage
